<template>
	<div
		@dblclick.capture="onDoubleClicked"
		@click="onClicked"
		@contextmenu="_onContextMenu"
	>
		<t-card
			background="white"
			elevation="1"
			border="default"
			class="trip-card"
			:class="{selected: selected}"
		>
			<div slot="header" class="trip-card-header">
				<div class="header-icon-display card-details">
					<t-timpex-icon
						:color="ordersIconColor"
						icon="orders"
						size="md"
					/>
					<div class="under-text">
						{{ ordersCount }}
					</div>
				</div>
				<div class="trip-card-order-count">
					{{trip.tripNumber}}
				</div>
				<t-metric-details
					:value="trip.volume"
					:max="getMaxVolume(trip)"
					hide-max
					class="card-details"
					size="xsmall"
					decimals="1"
					vertical
					unit="m3"
				/>
				<t-metric-details
					:value="trip.grossWeight"
					:max="getMaxWeight(trip)"
					hide-max
					class="card-details"
					size="xsmall"
					vertical
					unit="kg"
				/>
				<t-metric-details
					:value="trip.loadingMeters"
					:max="getMaxLoadingMeters(trip)"
					hide-max
					class="card-details"
					size="xsmall"
					decimals="1"
					vertical
					unit="lm"
				/>
				<div class="header-icon-display" :class="{'card-details': Boolean(registrationNumber)}">
					<t-timpex-icon
						:icon="vehicleIcon"
						:color="vehicleIconColor"
						size="sm"
					/>
					<div class="under-text">
						{{ registrationNumber }}
					</div>
				</div>
			</div>
			<div style="flex: 1;">
				<t-destinations-small :from="from" :to="to" />
			</div>
		</t-card>
		<t-popover
			ref="contextMenu"
			closeOnClick
			closeOnRightClick
			@open="contextMenuOpen = true"
			@close="contextMenuOpen = false;"
		>
			<slot name="contextMenu" :item="trip"/>
		</t-popover>
	</div>
</template>

<script>
import moment from 'moment';
export default {
	name: 'TTripCard',
	props: {
		/** Trip */
		trip: Object,
		/** Color of orders icon */
		ordersIconColor: { type: String, default: 'blue' },
		/** Icon name from TTimpexIcon for vehicle icon */
		vehicleIcon: String,
		/** Color for vehicle icon */
		vehicleIconColor: String,
		/** Selected */
		selected: Boolean,
		/** Scroll element into view on select */
		scrollIntoViewOnSelect: Boolean,
	},
	data() {
		return {
			clickInProgress: undefined,
			preventSingleClick: false,
			contextMenuOpen: false,
		};
	},
	computed: {
		ordersCount() {
			return this.trip.orders ? this.trip.orders.length : 0;
		},
		from() {
			return this.createDestination(this.trip.fromAddress, this.trip.etd, this.trip.atd);
		},
		to() {
			return this.createDestination(this.trip.toAddress, this.trip.eta, this.trip.ata);
		},
		registrationNumber() {
			if (this.trip && this.trip.cargoCarrier && this.trip.cargoCarrier.registrationNumber) return this.trip.cargoCarrier.registrationNumber;
			return '';
		}
	},
	watch: {
		selected(newVal) {
			if (newVal && this.scrollIntoViewOnSelect) {
				this.$el.scrollIntoView({ behavior: 'smooth', inline: 'center' });
			}
		}
	},
	methods: {
		createDestination(party, estimatedDate, actualDate) {
			if (party) {
				return {
					name: party.name,
					place: `${party.postalCode} ${party.postalPlace}`,
					estimatedDate: this.getFormattedDate(estimatedDate),
					actualDate: this.getFormattedDate(actualDate),
				};
			}
			return {};
		},
		getFormattedDate(dateObj) {
			if (dateObj && dateObj.epoch) {
				return moment(dateObj.epoch).format('DD.MM');
			}
			return '';
		},
		getMaxLoadingMeters(item) {
			if (item.cargoCarrier && item.cargoCarrier.loadingMeters)
				return item.cargoCarrier.loadingMeters;
			return undefined;
		},
		getMaxWeight(item) {
			if (item.cargoCarrier && item.cargoCarrier.weight)
				return item.cargoCarrier.weight;
			return undefined;
		},
		getMaxVolume(item) {
			if (item.cargoCarrier && item.cargoCarrier.volume)
				return item.cargoCarrier.volume;
			return undefined;
		},
		onClicked() {
			if (this.clickInProgress) return;
			this.clickInProgress = setTimeout(() => {
				if (!this.preventSingleClick) {
					if (this.selected) {
						this.$emit('deselect', this.trip);
					} else {
						this.$emit('select', this.trip);
					}
				}
				this.clickInProgress = undefined;
				this.preventSingleClick = false;
			}, 200);
		},
		onDoubleClicked() {
			this.preventSingleClick = true;
			if (!this.selected) {
				this.$emit('select', this.trip);
			}
			this.$emit('open', this.trip);
			clearTimeout(this.clickInProgress);
		},
		_onContextMenu(event) {
			let popover = this.$refs.contextMenu;
			if (this.$scopedSlots['contextMenu'] && popover) {
				popover.open({ top: event.clientY, left: event.clientX });
				event.preventDefault();
			}
		}
	}
};
</script>
<style lang="scss" scoped>
@import '~@/components/evergreen/scss/text';
@import '~@/components/evergreen/scss/colors';
.selected {
	box-shadow: 0 0 2px get-color($neutral-scale, 'N4A'),
				0 0 0 1px get-color($blue-scale, 'B8'),
				0 0 0 3px get-color($blue-scale, 'B5A') !important;
	cursor: pointer;
	.trip-card-header {
		background-color: get-color($blue, 'light');
		border-color: get-color($blue, 'light');
	}
}
.trip-card {
	width: 240px;
	height: 124px;
}
.trip-card:hover {
	box-shadow: 0 0 2px get-color($neutral-scale, 'N4A'),
				0 0 0 1px get-color($blue-scale, 'B7'),
				0 0 0 3px get-color($blue-scale, 'B4A');
	cursor: pointer;
	.trip-card-header {
		background-color: get-color($blue, 'light');
		border-color: get-color($blue, 'light');
	}
}
.trip-card-header {
	height: 45px;
	padding: 0px 8px 0px 5px;
	background-color: #edf0f2;
	display: flex;
	justify-content: space-between;
	border-bottom: 1px solid #E4E7EB;
	.trip-card-order-count {
		@extend %font-families-ui;
		@extend %text-small;
		color: get-color($text, 'default');
		font-weight: 500;
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
		user-select: none;
		margin: auto 0;
	}
	.card-details {
		margin-top: 12px;
	}
	.header-icon-display {
		display: grid;
		align-items: center;
		grid-auto-flow: row;
		justify-items: center;
		-ms-flex-line-pack: center;
		align-content: center;
		row-gap: 0px;
		.under-text {
			@extend %text-xsmall;
		}
	}
}
.trip-card-title {
	margin-left: 7px;
	color:#084b8a;
	font-size: 15px;
	font-weight: 600;
	font-style: normal;
	font-stretch: normal;
	line-height: 15px;
	letter-spacing: normal;
}
</style>


<docs>
```jsx
<div>
	<t-trip-card
		ordersCount="7"
		ordersIconColor="blue"
		vehicleIcon="trailer"
		vehicleIconColor="blue"
		:from="{name: 'Terminal Tiller', place: '7038 Tiller', date: '12.12'}"
		:to="{name: 'Terminal Ålesund', place: '6001 Ålesund', date: '20.12'}"
	/>
</div>
```
</docs>